import { ComponentProps, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { MultiSelect, sortSelectedFirst } from "@bonliva-crm/ui";
import { useLocale, useUpdateEffect } from "@hooks";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";

type Option = { label: string; value: string | null };

export const InitialsFirstNameFilter = (props: FilterWithFavouriteProps) => {
  const selectRef = useRef<HTMLInputElement>(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const { locale } = useLocale();
  const title = props.label ?? locale.letterFirstName;

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current?.focus();
    }
  }, [expanded]);

  const options = letters.map((opt) => ({
    label: opt.toUpperCase(),
    value: opt
  }));

  const clear = () => resetField(props.name);

  const commonProps: ComponentProps<typeof MultiSelect<Option>> = {
    options,
    name: props.name,
    value:
      field?.value?.map((v: string) => ({
        label: v.toUpperCase(),
        value: v
      })) ?? [],
    onChange: (selected) => {
      setValue(
        props.name,
        selected.map((s) => s.value),
        { shouldDirty: true }
      );
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 300,
    showBadge: field?.value !== null,
    optionType: "checkbox",
    isClearable: field?.value !== null,
    onClear: clear,
    sortOptionsOnMenuOpenFn: (options) =>
      sortSelectedFirst({
        options,
        value: field?.value,
        valueOptionEqualityFn: (value, option) => value === option.value
      }),
    closeMenuOnSelect: false,
    hidePlaceholderOnFocus: false,
    showOptionDivider: false
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};

const letters = "abcdefghijklmnopqrstuvwxyzåäö".split("");
